import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import {
  setAgreement,
  setWeddingContractData,
  weddingHallContract,
} from "@src/@slice/weddingHallContractSlice";
import BottomFixedModal from "@src/components/common/bottomFixedModal/BottomFixedModal";
import Ksnet from "@src/pages/ksnetPay/Ksnet";
import "./finalContractInfoModal.scss";
import dayjs from "dayjs";
import { UMOMENT_HALL } from "@src/types/realtimeBooking";
import { getProductPrice } from "@src/api/weddingHall/weddingHall";
import useUrlQueryString from "@src/utils/hooks/useUrlQueryString";
import axios from "@src/api/axios";
import { BASE_URL } from "@src/utils";

interface IFinalContractInfoModalProps {
  isShow: boolean;
  price: number;
  closeModal: () => void;
  paymentMethod: "card" | "account";
}

const FinalContractInfoModal = ({
  isShow,
  price,
  closeModal,
  paymentMethod,
}: IFinalContractInfoModalProps) => {
  const dispatch = useDispatch();
  const weddingContractData = useSelector(weddingHallContract).data.weddingContractData; // 예식계약서 정보들
  const { partnerName, individualHallName, weddingDate, weddingTime, guarantee } =
    weddingContractData;
  const weddingDayId = useUrlQueryString().get("id");
  const partnerUuid = useParams().uuid;
  const isUmomentHall = UMOMENT_HALL.has(partnerUuid);

  // 로딩 상태와 결제 진행 상태 관리
  const [isLoading, setIsLoading] = useState(false);
  const [canProceedToPayment, setCanProceedToPayment] = useState(false);
  const [showPriceChangeAlert, setShowPriceChangeAlert] = useState(false);

  const finalPriceCheckForUmomentHall = async () => {
    // 유모먼트 홀이 아니면 최종 가격 확인 X
    if (!isUmomentHall) {
      return;
    }
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    try {
      // 1. 첫 번째 가격 변동 확인 API 호출
      await axios.get<{ item: any }>(
        `${BASE_URL}/v3/wedding/day/remaining-wedding-days/${weddingDayId}/external/price`,
      );

      // 3. 가격 변동 없음 (200 응답) - 바로 결제 진행
      setCanProceedToPayment(true);
    } catch (error) {
      if (error.response?.status === 400) {
        // 2-1. 가격 변동 있음 (400 응답)
        try {
          // 2-2. 사용자에게 가격 변동 알림 및 동의항목 해제
          dispatch(setAgreement({ keyname: "weddingContract", checked: false }));
          dispatch(setAgreement({ keyname: "personalPolicy", checked: false }));
          setShowPriceChangeAlert(true);

          // 3초 후 알림창과 모달 닫기
          setTimeout(() => {
            setShowPriceChangeAlert(false);
            closeModal();
          }, 3000);

          // 2-1. 1초 후 재호출하여 200 응답 받기
          await new Promise((resolve) => setTimeout(resolve, 1000));
          await axios.get<{ item: any }>(
            `${BASE_URL}/v3/wedding/day/remaining-wedding-days/${weddingDayId}/external/price`,
          );

          // 2-3. getProductPrice 재호출하여 업데이트된 가격 정보 가져오기
          const updatedPriceData = await getProductPrice(weddingDayId);
          const updatedContractData = {
            ...updatedPriceData,
            weddingDayId: weddingDayId,
          };

          // 기존 계약 정보에 업데이트된 가격 정보 dispatch
          dispatch(setWeddingContractData({ data: updatedContractData }));
        } catch (retryError) {
          console.error("가격 정보 재조회 중 에러:", retryError);
          alert("가격 정보를 불러오는데 실패했습니다. 다시 시도해주세요.");
        }
      } else {
        console.error("예상치 못한 에러:", error);
        alert("오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // useEffect 제거 - finalPriceCheckForUmomentHall은 버튼 클릭 시에만 호출되어야 함

  return (
    <>
      <BottomFixedModal isShow={isShow} title="" hideModal={closeModal}>
        {isShow && (
          <div className="contract-final-info-sheet">
            <h2>{`마지막으로 계약 내역을 확인해주세요.`}</h2>
            <div className="table-wrap">
              <table>
                <tbody>
                  <tr>
                    <td>예식장소</td>
                    <td>
                      {partnerName} {individualHallName}
                    </td>
                  </tr>
                  <tr>
                    <td>예식일자</td>
                    <td>
                      {dayjs(weddingDate.replaceAll(".", "-")).format("YYYY년 MM월 DD일 ddd요일")}{" "}
                      {weddingTime}
                    </td>
                  </tr>
                  <tr>
                    <td>보증인원</td>
                    <td>최소 {guarantee}명</td>
                  </tr>
                </tbody>
              </table>
              <p className="gray500">
                * 현장 상황에 따라 잔여 일정이 실시간 변동될 수 있으며 웨딩홀과 확인 전화 후 계약이
                최종 확정됩니다.
              </p>
            </div>
            {isUmomentHall ? (
              canProceedToPayment ? (
                <Ksnet
                  name={`계약금 ${price?.toLocaleString()}원 결제`}
                  paymentMethod={paymentMethod}
                  autoTrigger={true}
                />
              ) : (
                <div className="btn-wrap">
                  <button
                    className={`btn btn-full bold btn-red500`}
                    onClick={finalPriceCheckForUmomentHall}
                    disabled={isLoading}
                  >
                    {`계약금 ${price?.toLocaleString()}원 결제`}
                  </button>
                </div>
              )
            ) : (
              // 일반 홀인 경우 바로 Ksnet 결제
              <Ksnet
                name={`계약금 ${price?.toLocaleString()}원 결제`}
                paymentMethod={paymentMethod}
              />
            )}
          </div>
        )}
      </BottomFixedModal>
      {showPriceChangeAlert && (
        <div className="price-change-alert-overlay">
          <div className="price-change-alert">
            <p>
              웨딩홀의 가격 정보가 변경되어 예식 계약서가 업데이트 되었습니다. 변경된 계약 내용을
              다시 확인해 주세요.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default FinalContractInfoModal;
